'use client';

import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import BaseModal from 'src/components/primitives/BaseModal/BaseModal';
import useModal from 'src/components/Modals/hooks/useModal';
import Button from 'src/components/primitives/Button/Button';
import Checkbox from 'src/components/primitives/Checkbox/Checkbox';
import Input from 'src/components/primitives/Input/Input';
import Select from 'src/components/primitives/Select/Select';
import MarketIcon from 'src/components/Utils/MarketIcon';
import {
	useCreateWhitelistAddress,
	useUpdateWhitelistAddress,
	useWhitelistAddresses,
} from 'src/hooks/query/useWhitelistAddresses';
import {
	type FunXyzAsset,
	fetchWithdrawalSupportedAssets,
} from 'src/hooks/useFunXyzActions';
import {
	getChainIconSymbol,
	CHAIN_ICON_MAP,
	isAssetAllowed,
} from 'src/constants/funXyzChains';
import {
	BITCOIN_CHAIN_ID,
	isValidAddressForChain,
} from '@velocity-exchange/common';
import { cj } from 'src/utils/classNames';
import InputUi from 'src/components/primitives/Input/InputUi';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

const LABEL_MIN = 4;
const LABEL_MAX = 20;

export interface AddressBookModalProps {
	mode: 'add' | 'edit';
	record?: {
		whitelistId: string;
		address: string;
		label: string;
		token: string;
		chainId: string;
	};
}

export default function AddressBookModal({
	mode,
	record,
}: AddressBookModalProps) {
	const { hide } = useModal();
	const isMobileScreenSize = useIsMobileScreenSize();
	const createMutation = useCreateWhitelistAddress();
	const updateMutation = useUpdateWhitelistAddress();
	const { data: existingAddresses = [] } = useWhitelistAddresses();

	const [label, setLabel] = useState(record?.label ?? '');
	const [token, setToken] = useState(record?.token ?? '');
	const [isUniversal, setIsUniversal] = useState(!record?.token);
	const [address, setAddress] = useState(record?.address ?? '');
	const [chainId, setChainId] = useState(record?.chainId ?? '1');
	const [touched, setTouched] = useState(false);

	const [destinationAssets, setDestinationAssets] = useState<FunXyzAsset[]>([]);

	useEffect(() => {
		fetchWithdrawalSupportedAssets()
			.then(({ destinationAssets: fetchedAssets }) =>
				setDestinationAssets(fetchedAssets)
			)
			.catch(() => {});
	}, []);

	// Build unique asset list with per-chain availability
	const uniqueAssets = useMemo(() => {
		const assetMap = new Map<
			string,
			{ symbol: string; availableChainIds: string[] }
		>();

		for (const asset of destinationAssets) {
			if (!isAssetAllowed(asset.chainId, asset.symbol)) continue;

			const normalizedSymbol = asset.symbol.toUpperCase();
			const existing = assetMap.get(normalizedSymbol);

			if (existing) {
				if (!existing.availableChainIds.includes(asset.chainId)) {
					existing.availableChainIds.push(asset.chainId);
				}
			} else {
				assetMap.set(normalizedSymbol, {
					symbol: asset.symbol,
					availableChainIds: [asset.chainId],
				});
			}
		}

		const assets = Array.from(assetMap.values());
		assets.sort((a, b) => {
			if (a.symbol.toUpperCase() === 'USDC') return -1;
			if (b.symbol.toUpperCase() === 'USDC') return 1;
			return a.symbol.localeCompare(b.symbol);
		});
		return assets;
	}, [destinationAssets]);

	// Derive chains from destination assets
	const chains = useMemo(() => {
		const chainMap = new Map<string, { chainId: string; chainName: string }>();
		for (const asset of destinationAssets) {
			if (chainMap.has(asset.chainId)) continue;
			const displayName =
				asset.chainId === BITCOIN_CHAIN_ID
					? 'Bitcoin (Segwit)'
					: asset.chainName;
			chainMap.set(asset.chainId, {
				chainId: asset.chainId,
				chainName: displayName,
			});
		}
		return Array.from(chainMap.values());
	}, [destinationAssets]);

	// Build chain list with availability based on selected asset
	const selectedAssetEntry = useMemo(
		() =>
			token
				? uniqueAssets.find(
						(a) => a.symbol.toUpperCase() === token.toUpperCase()
					)
				: undefined,
		[uniqueAssets, token]
	);

	const chainOptions = useMemo(() => {
		const availableChainIds = selectedAssetEntry
			? new Set(selectedAssetEntry.availableChainIds)
			: null;

		const options: { id: string; name: string; isAvailable: boolean }[] = [];
		for (const chain of chains) {
			if (!CHAIN_ICON_MAP[chain.chainId]) continue;
			options.push({
				id: chain.chainId,
				name: chain.chainName,
				// If no asset selected (universal), all chains are available
				isAvailable: availableChainIds
					? availableChainIds.has(chain.chainId)
					: true,
			});
		}

		options.sort((a, b) => {
			if (a.isAvailable && !b.isAvailable) return -1;
			if (!a.isAvailable && b.isAvailable) return 1;
			return a.name.localeCompare(b.name);
		});

		return options;
	}, [chains, selectedAssetEntry]);

	// Auto-correct chain when asset changes and current chain doesn't support it
	useEffect(() => {
		if (!selectedAssetEntry || isUniversal) return;

		if (selectedAssetEntry.availableChainIds.includes(chainId)) return;

		// Prefer Ethereum
		const ethChainId = '1';
		if (selectedAssetEntry.availableChainIds.includes(ethChainId)) {
			setChainId(ethChainId);
			return;
		}

		const firstAvailable = chainOptions.find((c) => c.isAvailable);
		if (firstAvailable) {
			setChainId(firstAvailable.id);
		}
	}, [selectedAssetEntry, chainId, chainOptions, isUniversal]);

	const handleChainSelect = useCallback(
		(newChainId: string) => {
			const chain = chainOptions.find((c) => c.id === newChainId);
			if (chain?.isAvailable) {
				setChainId(newChainId);
			}
		},
		[chainOptions]
	);

	// Validation
	const labelError = useMemo(() => {
		if (!touched || !label) return null;
		if (label.length < LABEL_MIN || label.length > LABEL_MAX) {
			return `Label must be between ${LABEL_MIN} and ${LABEL_MAX} characters`;
		}
		const isDuplicate = existingAddresses.some(
			(a) => a.label === label && a.whitelistId !== record?.whitelistId
		);
		if (isDuplicate) return 'Please use a different label';
		return null;
	}, [label, touched, existingAddresses, record?.whitelistId]);

	const addressError = useMemo(() => {
		if (!address) return null;
		if (!touched) return null;
		if (!isValidAddressForChain(address, chainId || null)) {
			return 'The address format is wrong. Please check the address length and character content and try again';
		}
		const isDuplicate = existingAddresses.some(
			(a) =>
				a.address.toLowerCase() === address.toLowerCase() &&
				a.chainId === chainId &&
				a.whitelistId !== record?.whitelistId
		);
		if (isDuplicate) return 'This address is already used for this chain';
		return null;
	}, [address, chainId, touched, existingAddresses, record?.whitelistId]);

	const canSubmit =
		!labelError &&
		label.length >= LABEL_MIN &&
		!addressError &&
		address.length > 0 &&
		isValidAddressForChain(address, chainId || null) &&
		!!chainId &&
		(isUniversal || !!token) &&
		!createMutation.isPending &&
		!updateMutation.isPending;

	const { mutateAsync: createAsync } = createMutation;
	const { mutateAsync: updateAsync } = updateMutation;

	const handleSubmit = useCallback(async () => {
		setTouched(true);
		if (!canSubmit) return;

		const params = {
			address,
			label,
			token: isUniversal ? '' : token,
			chainId,
		};

		try {
			if (mode === 'edit' && record?.whitelistId) {
				await updateAsync({
					whitelistId: record.whitelistId,
					...params,
				});
			} else {
				await createAsync(params);
			}
			hide();
		} catch {
			// Error handled by mutation onError callback
		}
	}, [
		canSubmit,
		address,
		label,
		isUniversal,
		token,
		chainId,
		mode,
		record,
		createAsync,
		updateAsync,
		hide,
	]);

	const isLoading = createMutation.isPending || updateMutation.isPending;
	const title = mode === 'edit' ? 'Edit Address Book' : 'Add to Address Book';

	const addressFormElementCommonProps = {
		id: 'address-book-address',
		value: address,
		onChange: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
			setAddress(e.target.value ?? '');
			setTouched(true);
		},
		placeholder: 'Enter address here',
	};

	const addressFormElement = isMobileScreenSize ? (
		<textarea
			{...addressFormElementCommonProps}
			className="resize-none"
			cols={2}
		/>
	) : (
		<input {...addressFormElementCommonProps} />
	);

	return (
		<BaseModal.Container onClose={hide} className="sm:min-w-120">
			<BaseModal.Title onClose={hide}>{title}</BaseModal.Title>
			<BaseModal.Body>
				<Input
					id="address-book-label"
					type="text"
					labelVariant="standard"
					label="Label"
					value={label}
					onChange={(val) => {
						setLabel(val ?? '');
						setTouched(true);
					}}
					placeholder="Enter your label's name here"
					error={labelError}
				/>

				<Select.Root value={chainId} onValueChange={handleChainSelect}>
					<Select.Trigger label="Chain">
						{chainId && (
							<div className="flex items-center gap-xs">
								<MarketIcon
									marketSymbol={getChainIconSymbol(chainId) ?? ''}
									className="size-lg shrink-0"
								/>
								<span>
									{chainOptions.find((c) => c.id === chainId)?.name ?? chainId}
								</span>
							</div>
						)}
					</Select.Trigger>
					<Select.Options className="max-h-[280px]" align="start">
						{chainOptions.map((chain) => (
							<Select.Option
								key={chain.id}
								value={chain.id}
								disabled={!chain.isAvailable}
							>
								<div className="flex items-center gap-xs">
									<MarketIcon
										marketSymbol={getChainIconSymbol(chain.id) ?? ''}
										className={cj(
											'size-lg flex-shrink-0',
											!chain.isAvailable && 'opacity-50'
										)}
									/>
									<span
										className={cj(!chain.isAvailable && 'text-text-disabled')}
									>
										{chain.name}
									</span>
								</div>
							</Select.Option>
						))}
					</Select.Options>
				</Select.Root>

				<InputUi.StackedWrapper>
					<InputUi.StandardLabel htmlFor={addressFormElementCommonProps.id}>
						Address
					</InputUi.StandardLabel>
					<InputUi.Container error={!!addressError}>
						<InputUi.Value size="sm" asChild>
							{addressFormElement}
						</InputUi.Value>
					</InputUi.Container>
					{addressError && <InputUi.Error>{addressError}</InputUi.Error>}
				</InputUi.StackedWrapper>

				{!isUniversal && (
					<Select.Root value={token} onValueChange={setToken}>
						<Select.Trigger label="Asset">
							{token && (
								<div className="flex items-center gap-xs">
									<MarketIcon
										marketSymbol={token}
										className="size-lg shrink-0"
									/>
									<span>{token}</span>
								</div>
							)}
						</Select.Trigger>
						<Select.Options className="max-h-[280px]" align="start">
							{uniqueAssets.map((asset) => (
								<Select.Option key={asset.symbol} value={asset.symbol}>
									<div className="flex items-center gap-xs">
										<MarketIcon
											marketSymbol={asset.symbol}
											className="size-lg shrink-0"
										/>
										<span>{asset.symbol}</span>
									</div>
								</Select.Option>
							))}
						</Select.Options>
					</Select.Root>
				)}

				<Checkbox.Default
					id="address-book-universal"
					checked={isUniversal}
					onCheckedChange={() => {
						if (!isUniversal) setToken('');
						setIsUniversal((prev) => !prev);
					}}
					label="Set as a universal address to receive any asset on the selected chain"
					labelClassName="whitespace-normal"
				/>
			</BaseModal.Body>

			<BaseModal.Footer>
				{mode === 'edit' && (
					<Button
						size="lg"
						variant="secondary"
						className="flex-1"
						onClick={hide}
						disabled={isLoading}
					>
						Cancel
					</Button>
				)}
				<Button
					size="lg"
					className="flex-1"
					onClick={handleSubmit}
					disabled={!canSubmit}
					isLoading={isLoading}
				>
					Save
				</Button>
			</BaseModal.Footer>
		</BaseModal.Container>
	);
}
